import { GeometryType, LineSegment } from "../../../../core/geo/types";
import { ChangeTypes, GeometryChange } from "../types";
import { isSegmentArcByAngles, isSegmentArcByEdge, isSegmentGeoBorder } from "../../../../core/geo/assertions";
import { coordinatesEqual, flattenCoordinates } from "../../../../common/util/draw/geometry";
import { numberComparator } from "../../../../core/comparators/numberComparator";
import { pickSegmentFields } from "./extractFields";
import { Feature, LineString, Polygon } from "geojson";
import Logger from "../../../../common/util/Logger";
import { getChangedPointWithIndex } from "../utils";
import { mapSegmentsToCoordinates } from "../../../../portal/geometry/map/mapSegment";

const logger = new Logger("geometryChange");

const segmentTypeChanged = (segmentBefore: LineSegment, segmentNow: LineSegment) =>
    segmentBefore.type !== segmentNow.type;

const arcByEdgeChange = (segmentBefore: LineSegment, segmentNow: LineSegment) => {
    return (
        isSegmentArcByEdge(segmentBefore) &&
        isSegmentArcByEdge(segmentNow) &&
        !coordinatesEqual(segmentBefore.properties.arcPoint, segmentNow.properties.arcPoint)
    );
};

const arcByAnglesChange = (segmentBefore: LineSegment, segmentNow: LineSegment) => {
    return (
        isSegmentArcByAngles(segmentBefore) &&
        isSegmentArcByAngles(segmentNow) &&
        (!coordinatesEqual(segmentBefore.properties.center, segmentNow.properties.center) ||
            !numberComparator(segmentBefore.properties.radius, segmentNow.properties.radius) ||
            !numberComparator(segmentBefore.properties.bearing1, segmentNow.properties.bearing1) ||
            !numberComparator(segmentBefore.properties.bearing2, segmentNow.properties.bearing2))
    );
};

const geoBorderChange = (segmentBefore: LineSegment, segmentNow: LineSegment) => {
    return (
        isSegmentGeoBorder(segmentBefore) &&
        isSegmentGeoBorder(segmentNow) &&
        (segmentBefore.properties.geoBorderId !== segmentNow.properties.geoBorderId ||
            segmentBefore.properties.index !== segmentNow.properties.index)
    );
};

const getSegmentChange = (
    segmentBefore: LineSegment,
    segmentNow: LineSegment,
    geometryType: GeometryType
): GeometryChange => {
    if (
        segmentTypeChanged(segmentBefore, segmentNow) ||
        arcByEdgeChange(segmentBefore, segmentNow) ||
        arcByAnglesChange(segmentBefore, segmentNow) ||
        geoBorderChange(segmentBefore, segmentNow)
    ) {
        const pickedFields = pickSegmentFields(segmentNow);
        return { type: ChangeTypes.SEGMENT_CHANGED, segment: pickedFields };
    }

    if (!isSegmentArcByAngles(segmentNow)) {
        if (!coordinatesEqual(segmentBefore.properties.point, segmentNow.properties.point)) {
            return {
                type:
                    geometryType === GeometryType.POLYGON
                        ? ChangeTypes.MOVE_POLYGON_POINT
                        : ChangeTypes.MOVE_LINE_POINT,
                coordinateChange: {
                    coordinates: segmentNow.properties.point,
                    pointIndex: segmentNow.properties.index,
                },
            };
        }
    }

    return { type: ChangeTypes.NONE };
};

const handlePointAdded = (
    featureBefore: Feature,
    featureNow: Feature,
    geometryType: GeometryType
): GeometryChange[] => {
    const change = getChangedPointWithIndex(
        mapSegmentsToCoordinates(featureBefore.properties!.segments),
        mapSegmentsToCoordinates(featureNow.properties!.segments)
    );
    logger.info(`GeometryChange: ${geometryType} feature point added: ${JSON.stringify(change)}`);
    return [
        {
            type: geometryType === GeometryType.POLYGON ? ChangeTypes.ADD_POLYGON_POINT : ChangeTypes.ADD_LINE_POINT,
            coordinateChange: change,
        },
    ];
};

const handlePointRemoved = (
    featureBefore: Feature,
    featureNow: Feature,
    geometryType: GeometryType
): GeometryChange[] => {
    const change = getChangedPointWithIndex(
        mapSegmentsToCoordinates(featureBefore.properties!.segments),
        mapSegmentsToCoordinates(featureNow.properties!.segments)
    );
    logger.info(`GeometryChange: ${geometryType} feature point removed: ${JSON.stringify(change)}`);
    return [
        {
            type:
                geometryType === GeometryType.POLYGON
                    ? ChangeTypes.REMOVE_POLYGON_POINT
                    : ChangeTypes.REMOVE_LINE_POINT,
            coordinateChange: change,
        },
    ];
};

const getCoordinates = (feature: Feature<LineString | Polygon>, geometryType: GeometryType) => {
    const newCoordinates = [...flattenCoordinates(feature.geometry.coordinates)];

    return geometryType === GeometryType.POLYGON ? newCoordinates.slice(0, -1) : newCoordinates;
};

const handleFeatureInserted = (
    featureNow: Feature<Polygon | LineString>,
    geometryType: GeometryType
): GeometryChange[] => {
    const newCoordinates = getCoordinates(featureNow, geometryType);
    logger.info(`GeometryChange: inserting feature of type: ${geometryType} with coordinates: ${newCoordinates}`);
    return [
        {
            type: geometryType === GeometryType.POLYGON ? ChangeTypes.INSERT_POLYGON : ChangeTypes.INSERT_LINE,
            coordinateChange: {
                coordinates: newCoordinates,
            },
        },
    ];
};

export const getSegmentChanges = (
    featureBefore: Feature<Polygon | LineString> | undefined,
    featureNow: Feature<Polygon | LineString>
) => {
    const geometryType = featureNow.geometry.type as GeometryType;
    const segmentsBefore = featureBefore?.properties?.segments;
    const segmentsNow = featureNow.properties!.segments;

    if (featureBefore === undefined) {
        return handleFeatureInserted(featureNow, geometryType);
    }
    if (segmentsBefore.length < segmentsNow.length) {
        return handlePointAdded(featureBefore, featureNow, geometryType);
    }
    if (segmentsBefore.length > segmentsNow.length) {
        return handlePointRemoved(featureBefore, featureNow, geometryType);
    }

    let changes = [];

    for (let i = 0; i < segmentsNow.length; i++) {
        const segmentChange = getSegmentChange(segmentsBefore[i], segmentsNow![i], geometryType);

        if (segmentChange.type !== ChangeTypes.NONE) {
            changes.push(segmentChange);
        }
    }
    logger.info("GeometryChange: " + JSON.stringify(changes));
    return changes;
};
